import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ChatStep } from '../../types/stream.types';
import { StepItem } from './steps/step-item';

function activitySummary(steps: ChatStep[]): { detail: string } {
  const tools = steps.filter((s) => s.type === 'tool_call');
  const thoughts = steps.filter((s) => s.type === 'thought');
  const parts: string[] = [];
  if (tools.length) parts.push(`${tools.length} tool${tools.length === 1 ? '' : 's'}`);
  if (thoughts.length) parts.push(`${thoughts.length} reasoning`);
  const detail = parts.length ? parts.join(' · ') : `${steps.length} step${steps.length === 1 ? '' : 's'}`;
  return { detail };
}

interface AgentActivityGroupProps {
  steps: ChatStep[];
  lastTimelineStep: ChatStep | undefined;
  isStreaming: boolean;
  startExpanded: boolean;
  onViewDocument?: (url: string, name: string) => void;
}

export function AgentActivityGroup({
  steps,
  lastTimelineStep,
  isStreaming,
  startExpanded,
  onViewDocument,
}: AgentActivityGroupProps) {
  const [open, setOpen] = useState(startExpanded);

  useEffect(() => {
    if (startExpanded) setOpen(true);
  }, [startExpanded]);

  useEffect(() => {
    if (!isStreaming) setOpen(false);
  }, [isStreaming]);

  const { detail } = activitySummary(steps);

  return (
    <div className="overflow-hidden rounded border border-slate-100 bg-slate-50/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[11px] text-slate-500 transition-colors hover:bg-slate-100/60"
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1 break-words leading-snug">
          <span className="font-medium text-slate-600">Activity</span>
          <span className="text-slate-300"> · </span>
          <span className="text-slate-500">{detail}</span>
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={`shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && (
        <div className="border-t border-slate-100/90 px-1.5 pb-1.5 pt-0.5">
          <div className="flex flex-col gap-0.5">
            {steps.map((step, idx) => {
              const isLast = step === lastTimelineStep;
              return (
                <StepItem
                  key={`${step.order}-${idx}-${step.type}-activity`}
                  step={step}
                  isLast={isLast}
                  isStreaming={isStreaming && isLast}
                  nestedInActivityGroup
                  onViewDocument={onViewDocument}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
